import { Component } from '@theme/component';
import { StandardEvents } from '@shopify/events';

/** How long a toast stays up (README: auto-hides after 4.2s). */
const DEFAULT_DURATION = 4200;

/**
 * @typedef {object} ToastAction
 * @property {string} label - Button label.
 * @property {() => void} onClick - Runs when the action is pressed; the toast closes first.
 */

/**
 * @typedef {object} ToastOptions
 * @property {'success' | 'error'} [tone] - Icon colour and live region (polite or assertive). Defaults to 'success'.
 * @property {string} [title] - Defaults to "Added to your bag" (success) or "Couldn't add to your bag" (error).
 * @property {string} [message] - Second line, e.g. "Lana Del Rey · Sophie".
 * @property {ToastAction | 'view-bag' | null} [action] - Defaults to "View bag" for success, none for errors.
 * @property {number} [duration] - Milliseconds before it hides. Defaults to 4200.
 */

/**
 * @typedef {object} Refs
 * @property {HTMLElement} toast - The card (a manual popover).
 * @property {HTMLElement} title
 * @property {HTMLElement} message
 * @property {HTMLButtonElement} action
 * @property {HTMLElement} status - Polite live region.
 * @property {HTMLElement} alert - Assertive live region.
 */

/**
 * @typedef {object} CartAjaxItem
 * @property {number | string} variant_id
 * @property {string} product_title
 * @property {Record<string, unknown> | null} [properties]
 */

/**
 * Fifi's V3 toast (DS Toast). Rendered once by snippets/fifi-toast.liquid.
 * Shows "Added to your bag" after Horizon's add-to-cart succeeds and an error toast when it fails.
 * Other modules use the exported showToast() / hideToast().
 *
 * @extends {Component<Refs>}
 */
export class FifiToast extends Component {
  requiredRefs = ['toast', 'title', 'message', 'action', 'status', 'alert'];

  /** @type {ReturnType<typeof setTimeout> | undefined} */
  #timer;

  #remaining = 0;

  #startedAt = 0;

  /** @type {(() => void) | null} */
  #onAction = null;

  connectedCallback() {
    super.connectedCallback();
    document.addEventListener(StandardEvents.cartLinesUpdate, this.#onCartLinesUpdate);
    document.addEventListener(StandardEvents.cartError, this.#onCartError);

    const { toast } = this.refs;
    toast.addEventListener('pointerenter', this.#pause);
    toast.addEventListener('pointerleave', this.#resume);
    toast.addEventListener('focusin', this.#pause);
    toast.addEventListener('focusout', this.#resume);
    toast.addEventListener('keydown', this.#onKeyDown);
  }

  disconnectedCallback() {
    super.disconnectedCallback();
    document.removeEventListener(StandardEvents.cartLinesUpdate, this.#onCartLinesUpdate);
    document.removeEventListener(StandardEvents.cartError, this.#onCartError);
  }

  /**
   * Shows the toast, replacing any toast that is already up.
   * @param {ToastOptions} [options]
   */
  show({ tone = 'success', title, message = '', action, duration = DEFAULT_DURATION } = {}) {
    this.#moveAboveOpenModal();

    const { toast, title: titleElement, message: messageElement, action: actionButton } = this.refs;
    const fallbackTitle = tone === 'error' ? this.dataset.errorTitle : this.dataset.addedTitle;
    const resolvedAction = action === undefined ? (tone === 'success' ? 'view-bag' : null) : action;

    toast.dataset.tone = tone;
    titleElement.textContent = title ?? fallbackTitle ?? '';
    messageElement.textContent = message;
    messageElement.hidden = !message;

    if (resolvedAction === 'view-bag') {
      actionButton.textContent = this.dataset.viewBagLabel ?? '';
      this.#onAction = openBag;
    } else if (resolvedAction) {
      actionButton.textContent = resolvedAction.label;
      this.#onAction = resolvedAction.onClick;
    } else {
      actionButton.textContent = '';
      this.#onAction = null;
    }
    actionButton.hidden = !this.#onAction;

    this.#announce(tone, [titleElement.textContent, message].filter(Boolean).join('. '));

    // Re-show so the entry animation replays and the card sits above any newer top-layer element.
    if (toast.matches(':popover-open')) toast.hidePopover();
    toast.showPopover();

    this.#remaining = duration;
    this.#resume();
  }

  /** Hides the toast. */
  hide = () => {
    clearTimeout(this.#timer);
    this.#timer = undefined;
    const { toast } = this.refs;
    if (toast.matches(':popover-open')) toast.hidePopover();
  };

  /** Runs the toast's action (e.g. "View bag") after closing it. */
  runAction = () => {
    const callback = this.#onAction;
    this.hide();
    callback?.();
  };

  /**
   * @param {'success' | 'error'} tone
   * @param {string} text
   */
  #announce(tone, text) {
    const region = tone === 'error' ? this.refs.alert : this.refs.status;
    region.textContent = '';
    // A fresh text node after a frame makes repeated messages announce again.
    requestAnimationFrame(() => {
      region.textContent = text;
    });
  }

  /**
   * A modal <dialog> (e.g. quick add) makes everything outside it inert, so the toast moves inside
   * the open one, and back to <body> when it closes.
   */
  #moveAboveOpenModal() {
    const modal = document.querySelector('dialog:modal');
    const home = modal ?? document.body;
    if (this.parentElement === home) return;

    home.append(this);
    modal?.addEventListener('close', () => document.body.append(this), { once: true });
  }

  #pause = () => {
    if (this.#timer === undefined) return;
    clearTimeout(this.#timer);
    this.#timer = undefined;
    this.#remaining = Math.max(0, this.#remaining - (performance.now() - this.#startedAt));
  };

  #resume = () => {
    if (!this.refs.toast.matches(':popover-open') || this.#timer !== undefined) return;
    if (this.refs.toast.matches(':hover, :focus-within')) return;
    this.#startedAt = performance.now();
    this.#timer = setTimeout(this.hide, this.#remaining);
  };

  /** @param {KeyboardEvent} event */
  #onKeyDown = (event) => {
    if (event.key !== 'Escape') return;
    event.stopPropagation();
    this.hide();
  };

  /**
   * Horizon's product form (product page and quick add) dispatches this before adding; its promise
   * resolves with the refreshed /cart.js items.
   * @param {import('@shopify/events').CartLinesUpdateEvent} event
   */
  #onCartLinesUpdate = (event) => {
    if (event.action !== 'add') return;

    const lines = /** @type {{ merchandiseId?: string }[] | undefined} */ (event.lines);
    const promise = /** @type {Promise<{ detail?: unknown }> | undefined} */ (event.promise);
    // Horizon passes the numeric variant id; tolerate a GID too.
    const variantId = String(lines?.[0]?.merchandiseId ?? '').split('/').pop();

    promise
      ?.then(({ detail }) => {
        const result = /** @type {{ didError?: boolean, items?: CartAjaxItem[] }} */ (detail ?? {});
        if (result.didError) return;
        // The bag is already showing the new line.
        if (document.querySelector('#cart-drawer[open]')) return;

        const item = result.items?.find((line) => String(line.variant_id) === variantId);
        this.show({ tone: 'success', message: item ? describeLine(item) : '' });
      })
      .catch(() => {});
  };

  /**
   * Add-to-cart failures from product forms. Bag line errors are shown inline by Horizon.
   * @param {import('@shopify/events').CartErrorEvent} event
   */
  #onCartError = (event) => {
    const { target } = event;
    if (!(target instanceof Element) || !target.closest('product-form-component')) return;

    const detail = /** @type {{ description?: string }} */ (event.detail ?? {});
    this.show({ tone: 'error', message: detail.description || event.error || '' });
  };
}

/**
 * "Product title · first two visible property values" (keys starting with "_" are hidden).
 * @param {CartAjaxItem} item
 * @returns {string}
 */
function describeLine(item) {
  const values = Object.entries(item.properties ?? {})
    .filter(([key, value]) => !key.startsWith('_') && typeof value === 'string' && value.trim() !== '')
    .slice(0, 2)
    .map(([, value]) => String(value));

  return [item.product_title, ...values].join(' · ');
}

/** Opens the bag drawer, or goes to the cart page where there is no drawer. */
function openBag() {
  const drawer = /** @type {(HTMLElement & { open?: () => void }) | null} */ (document.getElementById('cart-drawer'));

  if (drawer && typeof drawer.open === 'function') {
    drawer.open();
  } else {
    window.location.href = Theme.routes.cart_url;
  }
}

/** @returns {FifiToast | null} */
function getToast() {
  const element = document.querySelector('fifi-toast');
  return element instanceof FifiToast ? element : null;
}

/**
 * Shows the site toast. Safe to call before the element exists (does nothing).
 * @param {ToastOptions} [options]
 */
export function showToast(options) {
  getToast()?.show(options);
}

/** Hides the site toast. */
export function hideToast() {
  getToast()?.hide();
}

if (!customElements.get('fifi-toast')) {
  customElements.define('fifi-toast', FifiToast);
}
